import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Checkbox } from '~/components/form';
import { Button } from '~/components/ui/button';
import { formatRelativeTime } from '~/lib/datetime';

interface RefreshControlsProps {
  onRefresh: () => void;
  fetching: boolean;
  /** When the data last arrived (ms since epoch); `undefined` until the first load. */
  updatedAt?: number;
  auto: boolean;
  onAutoChange: (auto: boolean) => void;
  /** The page's subject (`services`): the test ids become `<subject>-refresh-button` and `<subject>-auto-refresh-toggle`. */
  subject: string;
}

/** A live page's Refresh button, Auto refresh switch and "Updated N ago", for `PageHeader extra` (DESIGN.md §7.6). */
export function RefreshControls({
  onRefresh,
  fetching,
  updatedAt,
  auto,
  onAutoChange,
  subject,
}: RefreshControlsProps) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 5000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="flex items-center gap-3">
      {updatedAt !== undefined && (
        <span className="text-sm text-gray-500 dark:text-gray-400" aria-live="off">
          Updated {formatRelativeTime(updatedAt, now)}
        </span>
      )}
      <Checkbox
        id={`${subject}-auto-refresh`}
        text="Auto refresh"
        checked={auto}
        onChange={onAutoChange}
        testId={`${subject}-auto-refresh-toggle`}
      />
      <Button
        variant="outline"
        size="sm"
        onClick={onRefresh}
        disabled={fetching}
        startIcon={<RefreshCw className={`size-4 ${fetching ? 'animate-spin' : ''}`} />}
        testId={`${subject}-refresh-button`}
      >
        Refresh
      </Button>
    </div>
  );
}
